import { z } from 'zod';
import { PackOverrideSchema } from './modpack.js';
import { LauncherManifestSchema } from './launcher.js';

export const ClientPackSchema = z
  .object({
    files: LauncherManifestSchema.shape.files,
    overrides: z.array(PackOverrideSchema).optional(),
    modpack: LauncherManifestSchema.shape.modpack,
    mcVersion: z.string().min(1),
    loader: z.enum(['vanilla', 'fabric', 'forge', 'neoforge']),
    loaderVersion: z.string().optional(),
    javaMajor: z.number().int().min(8).max(99),
    mods: LauncherManifestSchema.shape.mods.max(5000),
  })
  .superRefine((pack, context) => {
    if (pack.loader === 'vanilla' ? pack.loaderVersion !== undefined || pack.mods.length > 0 : !pack.loaderVersion)
      context.addIssue({
        code: 'custom',
        path: ['loaderVersion'],
        message: 'loaderVersion and mods must match loader',
      });
  });
export type ClientPack = z.infer<typeof ClientPackSchema>;
export const ClientPackResultSchema = z.object({
  ok: z.boolean(),
  reason: z.string().max(4000).optional(),
  logs: z.array(z.string().max(2000)).max(500),
  crashReports: z.array(z.object({ name: z.string(), content: z.string().max(65536) })).max(5),
});
export type ClientPackResult = z.infer<typeof ClientPackResultSchema>;
export const ClientPackProblemSchema = z.object({
  projectId: z.string().optional(),
  modId: z.string().optional(),
  title: z.string().optional(),
  kind: z.enum(['dependency', 'conflict', 'duplicate', 'unavailable', 'crash', 'other']),
  message: z.string(),
  dependencyId: z.string().optional(),
});
export type ClientPackProblem = z.infer<typeof ClientPackProblemSchema>;
export const ClientPackModDeclarationSchema = z.object({ projectId: z.string(), modId: z.string(), title: z.string() });
export type ClientPackModDeclaration = z.infer<typeof ClientPackModDeclarationSchema>;
export const ClientPackVerificationSchema = z.object({
  problems: z.array(ClientPackProblemSchema).optional(),
  modDeclarations: z.array(ClientPackModDeclarationSchema).optional(),
  hash: z.string(),
  state: z.enum(['queued', 'running', 'succeeded', 'failed']),
  createdAt: z.string(),
  finishedAt: z.string().optional(),
  reason: z.string().optional(),
  /** テスト用 VM の空きを待ち始めた時刻。長く空かなければ待ちをやめる */
  waitingSince: z.string().optional(),
  added: z.array(z.string()).default([]),
  unavailable: z
    .array(z.object({ projectId: z.string().optional(), title: z.string(), reason: z.string().optional() }))
    .default([]),
  stages: z
    .object({
      preflight: ClientPackResultSchema.shape.logs,
      vm: ClientPackResultSchema.shape.logs,
      launch: ClientPackResultSchema.shape.logs,
    })
    .optional(),
  logs: ClientPackResultSchema.shape.logs,
  crashReports: ClientPackResultSchema.shape.crashReports,
});
export type ClientPackVerification = z.infer<typeof ClientPackVerificationSchema>;
export const ClientTemplateVerificationSchema = z.object({
  serverId: z.string(),
  serverName: z.string(),
  mcVersion: z.string().optional(),
  verification: ClientPackVerificationSchema,
});
export type ClientTemplateVerification = z.infer<typeof ClientTemplateVerificationSchema>;

/** Installation and menu detection have independent deadlines; transport includes cleanup slack. */
export const CLIENT_INSTALL_TIMEOUT_MS = 15 * 60 * 1000;
// 実機（テンプレート 9010、4 コア、llvmpipe）で 26.3 + Fabric + Mod 8 個は約 2 分。初回や混雑時の余裕をとって 8 分
export const CLIENT_LAUNCH_TIMEOUT_MS = 480 * 1000;
/**
 * 起動テストのゲームと VM のメモリ。Mod が多い Modpack は 2GB では足りない（Improved Oritech は 196 個）。
 * VM には、ゲームのヒープに加えて、ソフトウェア描画（llvmpipe）・Xvfb・OS の分を足す。
 * Mod が 300 個を超えると、ヒープの外（llvmpipe のテクスチャなど）だけで 2GB を超える（Create: Aeronautics は 387 個で、
 * 9216MB の VM で Java の RSS が 8.2GB を超えて OOM になった）ので、ヒープは同じまま VM を広げる
 */
export function clientTestMemory(modCount: number) {
  const heapMb = modCount > 150 ? 6144 : modCount > 50 ? 4096 : 2048;
  const vmMemoryMb = heapMb === 2048 ? 4096 : modCount > 300 ? heapMb + 6144 : heapMb + 3072;
  return { heapMb, swapMb: heapMb, vmMemoryMb };
}
export const CLIENT_VERIFY_TIMEOUT_MS = CLIENT_INSTALL_TIMEOUT_MS + CLIENT_LAUNCH_TIMEOUT_MS + 120 * 1000;

/**
 * クライアント起動テストの進み具合の目安（%）。保存している段階ごとの記録と、その文言から決める。
 * 終わったテストは undefined。Mod の準備の間は、準備できた件数に比例させる
 */
export function clientPackProgress(
  check: Pick<ClientPackVerification, 'state' | 'stages' | 'reason'>,
): { percent: number; label: string } | undefined {
  if (check.state === 'queued') return { percent: 0, label: check.reason ?? '順番を待っています' };
  if (check.state !== 'running') return undefined;
  const vm = check.stages?.vm ?? [];
  const launch = check.stages?.launch ?? [];
  const has = (lines: string[], text: string) => lines.some((line) => line.includes(text));
  if (has(launch, 'タイトル画面を確認します'))
    return { percent: 90, label: 'ゲームを起動して、タイトル画面を待っています' };
  if (has(launch, 'インストール完了')) return { percent: 85, label: 'ゲームを起動しています' };
  if (has(launch, 'Minecraft とローダーをインストール'))
    return { percent: 75, label: 'Minecraft とローダーをインストールしています' };
  // Mod は 1 件ずつ「取り置きから再利用」か「ダウンロード」の行が出る。全体の件数は事前チェックの記録にある
  const total = Number(
    (check.stages?.preflight ?? [])
      .map((line) => /Mod (\d+) 件/.exec(line)?.[1])
      .findLast((value) => value !== undefined) ?? 0,
  );
  const prepared = launch.filter((line) => /^(取り置きから再利用|ダウンロード): /.test(line)).length;
  if (total > 0 && prepared > 0)
    return {
      percent: 40 + Math.round(30 * Math.min(1, prepared / total)),
      label: `Mod を準備しています（${Math.min(prepared, total)} / ${total} 件）`,
    };
  if (launch.length) return { percent: 40, label: 'Java と Mod を準備しています' };
  if (has(vm, '起動テストを開始')) return { percent: 35, label: '起動テストを始めています' };
  if (has(vm, 'agent に接続')) return { percent: 30, label: 'テスト用 VM の agent に接続しています' };
  if (has(vm, '起動:')) return { percent: 25, label: 'テスト用 VM を起動しています' };
  if (has(vm, '作成:')) return { percent: 20, label: 'テスト用 VM を作成しています' };
  if (vm.length) return { percent: 15, label: 'テスト用 VM を準備しています' };
  return { percent: 5, label: 'Mod の依存関係と jar を確かめています' };
}

/** 動いているテスト用 VM（「起動: <ノード> / VM <番号>」の記録から読む）。後片付けが済んだら返さない */
export function clientPackTestVm(
  check: Pick<ClientPackVerification, 'state' | 'stages'>,
): { node: string; vmid: number } | undefined {
  if (check.state !== 'running') return undefined;
  const vm = check.stages?.vm ?? [];
  const started = vm.findLastIndex((line) => /^起動: \S+ \/ VM \d+$/.test(line));
  if (started < 0 || vm.slice(started).some((line) => line.includes('後片付けが完了'))) return undefined;
  const [, node, vmid] = /^起動: (\S+) \/ VM (\d+)$/.exec(vm[started]!)!;
  return { node: node!, vmid: Number(vmid) };
}

export const ClientPackCheckSummarySchema = ClientPackVerificationSchema.pick({
  state: true,
  createdAt: true,
  finishedAt: true,
  reason: true,
}).extend({
  serverId: z.string(),
  serverName: z.string(),
  mcVersion: z.string(),
  progress: z.object({ percent: z.number().min(0).max(100), label: z.string() }).optional(),
  /** 同じ構成のサーバーは 1 回のテストにまとめる。同じ値の行は同じテスト */
  test: z.string().optional(),
  /** クライアント Mod テンプレートを選んでいるサーバーのとき、その名前 */
  templateName: z.string().optional(),
  /** 実行中のテスト用 VM と、割り当てたメモリ */
  vm: z.object({ node: z.string(), vmid: z.number(), memoryMb: z.number() }).optional(),
});
export type ClientPackCheckSummary = z.infer<typeof ClientPackCheckSummarySchema>;
